import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { showSuccessPopup, showFailurePopup, checkPaymentStatus, PaymentStatusResponse } from '../services/paymentService';

const getPaymentStatusText = (paymentStatus: PaymentStatusResponse): string => {
  if (typeof paymentStatus === 'string') {
    return paymentStatus.trim().toUpperCase();
  }

  return (
    paymentStatus.order_status ||
    paymentStatus.status ||
    paymentStatus.paymentStatus ||
    ''
  ).trim().toUpperCase();
};

const PaymentStatusPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        // Log URL parameters for debugging
        console.log('Complete URL:', window.location.href);

        if (!orderId) {
          throw new Error('Order ID not found in URL');
        }

        // Get payment status from your API
        const paymentStatus = await checkPaymentStatus(orderId);
        console.log('Payment status details:', paymentStatus);

        if (!paymentStatus) {
          throw new Error('Failed to get payment status');
        }

        // Check if payment is successful (PAID status)
        const statusText = getPaymentStatusText(paymentStatus);
        const isSuccess = statusText === 'PAID' || statusText === 'SUCCESS';

        console.log('Payment success check:', {
          orderStatus: statusText,
          orderId: typeof paymentStatus === 'string' ? orderId : paymentStatus.order_id,
          amount: typeof paymentStatus === 'string' ? undefined : paymentStatus.order_amount,
          isSuccess: isSuccess
        });

        // Show appropriate popup and redirect
        if (isSuccess) {
          console.log('Payment successful! Showing success popup');
          showSuccessPopup(orderId);
          setTimeout(() => navigate(`/order-summary/${orderId}`), 1200);
        } else {
          console.log('Payment failed! Showing failure popup');
          showFailurePopup(`Payment Failed`, orderId);
          setTimeout(() => navigate(`/order-summary/${orderId}`), 3000);
        }
      } catch (error) {
        console.error('Error processing payment status:', error);
        setError(error instanceof Error ? error.message : 'Failed to process payment');
        showFailurePopup('Error processing payment status', orderId || '');
        setTimeout(() => navigate(`/order-summary/${orderId}`), 3000);
      }
    };

    checkStatus();
  }, [navigate, orderId]);

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4 text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-700">{error}</p>
          <p className="text-sm text-gray-500 mt-2">Redirecting to order summary...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-minto-500 mx-auto"></div>
        <p className="mt-4 text-gray-700">Verifying payment status...</p>
      </div>
    </div>
  );
};

export default PaymentStatusPage; 
